import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
	searchFreelancerActivityReports,
	updateFreelancerActivityReport,
	bulkChangeFreelancerActivityReportStatus,
	getActivityReportsSummary
} from "../api/freelancer-activity-reports-api";
import { FreelancerActivityReport } from "../models/FreelancerActivityReport";
import { ActivityReportsSummaryResponse } from "../models/ActivityReportsSummary";
import { FreelancerActivityReportSearchRequest } from "../models/requests/FreelancerActivityReportSearchRequest";
import { UpdateFreelancerActivityReportRequest } from "../models/requests/UpdateFreelancerActivityReportRequest";
import { BulkChangeFreelancerActivityReportStatusRequest } from "../models/requests/BulkChangeFreelancerActivityReportStatusRequest";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import { useLinearProgressContext } from "../contexts/useLinearProgressContext";

/**
 * Hook to search freelancer activity reports
 */
export const useSearchFreelancerActivityReports = (
	searchRequest: FreelancerActivityReportSearchRequest,
	freelancerActivityReportKey: string = "",
	enabled: boolean = true
) => {
	return useQuery<FreelancerActivityReport[], Error>({
		queryKey: ["freelancer-activity-reports", "search", searchRequest],
		queryFn: () =>
			searchFreelancerActivityReports(searchRequest, freelancerActivityReportKey),
		enabled
	});
};

/**
 * Hook to get activity reports summary for a project
 */
export const useActivityReportsSummary = (
	projectId: number,
	enabled: boolean = true
) => {
	return useQuery<ActivityReportsSummaryResponse, Error>({
		queryKey: ["activity-reports-summary", projectId],
		queryFn: () => getActivityReportsSummary(projectId),
		enabled: enabled && projectId > 0
	});
};

/**
 * Hook to update a freelancer activity report
 */
export const useUpdateFreelancerActivityReport = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation<
		FreelancerActivityReport,
		Error,
		{ id: number; request: UpdateFreelancerActivityReportRequest }
	>({
		mutationFn: ({ id, request }) => {
			showProgress();
			return updateFreelancerActivityReport(id, request);
		},
		onSuccess: () => {
			hideProgress();

			showSuccess(t("activityReports.reportUpdatedSuccess"));

			// Invalidate all activity report queries
			queryClient.invalidateQueries({ queryKey: ["freelancer-activity-reports"] });
		},
		onError: (error: any) => {
			hideProgress();

			console.error("Error updating report:", error);

			// Extract validation error message from backend (Ardalis Result format)
			const validationErrors = error?.response?.data?.validationErrors;
			if (validationErrors?.length > 0) {
				const errorMessage = validationErrors[0]?.errorMessage;
				if (errorMessage?.includes("exceeds remaining")) {
					showError(t("activityReports.workedUnitsExceedsContractual"));
					return;
				}
			}

			showError(t("activityReports.reportUpdateError"));
		}
	});
};

/**
 * Hook to bulk change status of freelancer activity reports
 */
export const useBulkChangeFreelancerActivityReportStatus = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation<void, Error, BulkChangeFreelancerActivityReportStatusRequest[]>({
		mutationFn: requests => {
			showProgress();
			return bulkChangeFreelancerActivityReportStatus(requests);
		},
		onSuccess: () => {
			hideProgress();

			showSuccess(t("activityReports.reportSubmittedSuccess"));

			// Invalidate all activity report queries
			queryClient.invalidateQueries({ queryKey: ["freelancer-activity-reports"] });
		},
		onError: (error: Error) => {
			hideProgress();

			console.error("Error submitting report:", error);

			showError(t("activityReports.reportSubmitError"));
		}
	});
};
